import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '../../../services/translation.service';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../services/api/user/user.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { BaseTableComponent } from '../../../core/table/basetable';
import { UserList, UserListData, UserListDataArray } from '../../../model/user/users-list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { EmailLicense } from '../../../model/email-license/email-license';

@Component({
  selector: 'app-user',
  standalone: true,
  templateUrl:'./user.component.html',
  styleUrls: ['./user.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzDrawerModule,
    NzFormModule,
    NzSwitchModule,
    NzTagModule,
    NzPopconfirmModule
  ],
})
export class UserComponent extends BaseTableComponent implements OnInit, OnDestroy {
  protected readonly i18n = inject(TranslationService);
  private readonly userService = inject(UserService);
  private readonly message = inject(NzMessageService);
  private readonly router = inject(Router);
  private _subscriptions: Array<Subscription> = [];
  protected userList: UserList[] = [];
  protected searchTerm = '';
  protected selectedUser?: UserList;
  protected editModel: UserList = new UserList();
  protected isDrawerOpen = false;
  protected isEditMode = false;
  protected licenseStart = '';
  protected licenseDuration = 365;

  ngOnInit(): void {
    this.getUsers();
  }

  protected getUsers(): void {
    this._loading = true;
    const sub = this.userService.GETPaginatedUsers(this._paging).subscribe({
      next: (response: UserListDataArray) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('regulation.error.getAll'));
          this._loading = false;
          return;
        }
        this.userList = response.data.items;
        this._totalItems = response.data.count;
        this._loading = false;
      },
      error: (error: any) => {
        console.error('Error fetching users:', error);
        this._loading = false;
        if (error.status === 401) {
          this.router.navigate(['/']);
        }
      }
    });
    this._subscriptions.push(sub);
  }

  protected postUser(): void {
    this._subscriptions.push(this.userService.POSTUser(this.editModel).subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.createError'));
          return;
        }
        this.message.success(this.i18n.translate('user.messages.created'));
        this.getUsers();
        this.closeDrawer();
      },
      error: (error: any) => {
        console.error('Error creating user:', error);
        this.message.error(this.i18n.translate('user.messages.createError'));
      }
    }));
  }

  protected putUser(): void {
    if (!this.selectedUser?.userId) {
      return;
    }
    this._subscriptions.push(this.userService.PUTUser(this.selectedUser.userId, this.editModel).subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.updateError'));
          return;
        }
        this.message.success(this.i18n.translate('user.messages.updated'));
        this.getUsers();
        this.closeDrawer();
      },
      error: (error: any) => {
        console.error('Error updating user:', error);
        this.message.error(this.i18n.translate('user.messages.updateError'));
      }
    }));
  }

  protected onSearchTermChange(): void {
    this._paging.pageIndex = 1;
  }

  protected get filteredUsers(): UserList[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.userList;
    }
    return this.userList.filter((user) => {
      return [
        user.name,
        user.lastName,
        user.email,
        user.companyName,
        user.piva,
        user.city,
        user.userKey
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term));
    });
  }

  protected openDetails(user: UserList): void {
    this.openDrawer(user, false);
  }

  protected openEdit(user: UserList): void {
    this.openDrawer(user, true);
  }

  protected openCreate(): void {
    this.selectedUser = undefined;
    this.editModel = new UserList();
    this.isEditMode = true;
    this.isDrawerOpen = true;
    this.licenseStart = '';
    this.licenseDuration = 365;
  }

  protected closeDrawer(): void {
    this.isDrawerOpen = false;
  }

  protected saveUser(): void {
    if (!this.isEditMode) {
      return;
    }
    if (this.selectedUser) {
      this.putUser();
    } else {
      this.postUser();
    }
  }

  protected deleteUser(user: UserList): void {
    if (!user.userId) {
      return;
    }
    const sub = this.userService.DELETEUser(user.userId).subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.deleteError'));
          return;
        }
        this.userList = this.userList.filter((item) => item.userId !== user.userId);
        this._totalItems = Math.max(0, this._totalItems - 1);
        this.message.success(this.i18n.translate('user.messages.deleted'));
        if (this.selectedUser?.userId === user.userId) {
          this.closeDrawer();
          this.selectedUser = undefined;
        }
      },
      error: (error: any) => {
        console.error('Error deleting user:', error);
        this.message.error(this.i18n.translate('user.messages.deleteError'));
      }
    });
    this._subscriptions.push(sub);
  }

  protected putChangeEnabled(userId: string | undefined, enabled: boolean): void {
    if (!userId) {
      return;
    }
    const sub = this.userService.PUTUserStatus(userId, enabled).subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.updateError'));
          return;
        }
        if (this.selectedUser?.userId === userId) {
          this.editModel.enabled = enabled;
        }
        this.message.success(this.i18n.translate('user.messages.updated'));
      },
      error: (error: any) => {
        console.error('Error updating user status:', error);
        this.message.error(this.i18n.translate('user.messages.updateError'));
      }
    });
    this._subscriptions.push(sub);
  }

  protected postSendUserEmail(userId: string | undefined, email: string | undefined, userKey: string | undefined, startLicense: string, duration: number): void {
    if (!userId || !email || !userKey || !startLicense || !duration) {
      this.message.error(this.i18n.translate('user.messages.missingEmailData'));
      return;
    }
    const payload: EmailLicense = { email, userKey, startLicense, duration };
    const sub = this.userService.POSTSendLicenseToUser(payload, userId).subscribe({
      next: (response: any) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.emailError'));
          return;
        }
        this.message.success(this.i18n.translate('user.messages.emailSent'));
      },
      error: (error: any) => {
        console.error('Error sending license email:', error);
        this.message.error(this.i18n.translate('user.messages.emailError'));
      }
    });
    this._subscriptions.push(sub);
  }

  private openDrawer(user: UserList, editMode: boolean): void {
    this.selectedUser = user;
    this.editModel = this.cloneUser(user);
    this.isEditMode = editMode;
    this.isDrawerOpen = true;
    this.licenseStart = '';
    this.licenseDuration = 365;
    if (!user.userId) {
      return;
    }
    const sub = this.userService.GETUserByID(user.userId).subscribe({
      next: (response: UserListData) => {
        if (!response.success) {
          this.message.error(this.i18n.translate('user.messages.detailError'));
          return;
        }
        this.editModel = this.cloneUser({
          ...response.data,
          userId: user.userId
        } as UserList);
      },
      error: (error: any) => {
        console.error('Error fetching user detail:', error);
        this.message.error(this.i18n.translate('user.messages.detailError'));
      }
    });
    this._subscriptions.push(sub);
  }

  private cloneUser(user: UserList): UserList {
    const clone = new UserList();
    Object.assign(clone, user);
    return clone;
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }
}
